import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CoolifyStatusDto, MetricsStatusDto } from '@cpm/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useSystemStatus } from '../api/auth.js';
import { HelpLink } from '../components/help.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Textarea,
} from '../components/ui.js';

interface CoolifySettings {
  baseUrl: string | null;
  hasToken: boolean;
}

interface RecoverySettings {
  enabled: boolean;
  ready: boolean;
  reason: 'disabled' | 'app_url' | 'smtp' | null;
}

function RecoverySettingsCard() {
  const cache = useQueryClient();
  const state = useQuery({ queryKey: ['auth', 'recovery-settings'],
    queryFn: () => api.get<RecoverySettings>('/api/auth/recovery-settings') });
  const [notice, setNotice] = useState('');
  const refresh = () => cache.invalidateQueries({ queryKey: ['auth', 'recovery-settings'] });
  const save = useMutation({
    mutationFn: (enabled: boolean) => api.put<RecoverySettings>('/api/auth/recovery-settings', { enabled }),
    onSuccess: async () => { await refresh(); setNotice('Recovery setting saved. Send a test and confirm delivery.'); },
  });
  const test = useMutation({
    mutationFn: () => api.post('/api/auth/recovery-settings/test', {}),
    onSuccess: () => setNotice('Test sent to your login email. Confirm it arrived before relying on recovery links.'),
  });
  return <Card>
    <CardHeader title="Password recovery" description="Email one-use password links to panel users."
      actions={<Badge tone={state.data?.ready ? 'green' : 'yellow'}>{state.data?.ready ? 'ready' : 'not ready'}</Badge>} />
    <div className="space-y-4 px-5 py-4 text-sm">
      <p>Recovery email uses the TLS SMTP connection configured under <Link className="text-brand-600" to="/notifications">Notifications</Link>, independently of whether operational alerts are enabled. The server also needs a public APP_URL.</p>
      {state.data?.reason === 'smtp' && <p className="text-amber-700">Configure the SMTP connection and sender address first.</p>}
      {state.data?.reason === 'app_url' && <p className="text-amber-700">Set APP_URL to the panel's public HTTPS address first.</p>}
      <label className="flex items-center gap-2"><input type="checkbox" checked={state.data?.enabled ?? false}
        disabled={save.isPending || state.isPending} onChange={(event) => save.mutate(event.target.checked)} />
        Enable password recovery email</label>
      <Button type="button" variant="secondary" disabled={!state.data?.ready || test.isPending} onClick={() => test.mutate()}>Send recovery email test</Button>
      {notice && <p role="status">{notice}</p>}
      {(state.error || save.error || test.error) && <ErrorNote error={state.error ?? save.error ?? test.error} />}
    </div>
  </Card>;
}

function CoolifyConnectionCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['coolify', 'settings'],
    queryFn: () => api.get<CoolifySettings>('/api/coolify/settings'),
  });

  const [baseUrl, setBaseUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [testResult, setTestResult] = useState<CoolifyStatusDto | null>(null);

  useEffect(() => {
    if (settings.data?.baseUrl) setBaseUrl(settings.data.baseUrl);
  }, [settings.data?.baseUrl]);

  const test = useMutation({
    mutationFn: () =>
      api.post<CoolifyStatusDto>('/api/coolify/test', {
        baseUrl: baseUrl || undefined,
        apiToken: apiToken || undefined,
      }),
    onSuccess: setTestResult,
  });

  const save = useMutation({
    mutationFn: () =>
      api.put<CoolifyStatusDto>('/api/coolify/settings', {
        baseUrl,
        apiToken: apiToken || undefined,
      }),
    onSuccess: (status) => {
      setTestResult(status);
      setApiToken('');
      void queryClient.invalidateQueries({ queryKey: ['coolify'] });
      void queryClient.invalidateQueries({ queryKey: ['system', 'dashboard'] });
    },
  });

  return (
    <Card>
      <CardHeader
        title="Coolify connection"
        description="Create an API token in Coolify under Security → API Tokens (read + write + deploy; add read:sensitive for usage metrics — see docs)."
        actions={
          settings.data?.hasToken ? <Badge tone="green">Token stored</Badge> : <Badge tone="yellow">No token</Badge>
        }
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-4 px-5 py-4"
      >
        <div>
          <Label htmlFor="coolify-url">Coolify URL</Label>
          <Input
            id="coolify-url"
            type="url"
            placeholder="https://coolify.example.com"
            required
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="coolify-token">API token</Label>
          <Input
            id="coolify-token"
            type="password"
            placeholder={settings.data?.hasToken ? '•••••••• (leave blank to keep current)' : 'Paste your token'}
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Stored encrypted (AES-256-GCM). Never displayed again after saving.
          </p>
        </div>

        {testResult && (
          <div
            className={`rounded-lg px-4 py-3 text-sm ring-1 ring-inset ${
              testResult.reachable
                ? 'bg-emerald-50 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 ring-red-600/10 dark:bg-red-500/10 dark:text-red-400'
            }`}
          >
            {testResult.reachable
              ? `Connected — Coolify ${testResult.coolifyVersion}`
              : `Connection failed: ${testResult.error}`}
          </div>
        )}
        {(test.error ?? save.error) != null && <ErrorNote error={test.error ?? save.error} />}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            loading={test.isPending}
            onClick={() => test.mutate()}
            disabled={!baseUrl || (!apiToken && !settings.data?.hasToken)}
          >
            Test connection
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!baseUrl}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PanelSettingsCard() {
  const queryClient = useQueryClient();
  const status = useSystemStatus();
  const [panelName, setPanelName] = useState('');

  useEffect(() => {
    if (status.data?.panelName) setPanelName(status.data.panelName);
  }, [status.data?.panelName]);

  const save = useMutation({
    mutationFn: () => api.patch<{ panelName: string }>('/api/system/settings', { panelName }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['system', 'status'] }),
  });

  return (
    <Card>
      <CardHeader title="Panel" description="Branding for this installation." />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-4 px-5 py-4"
      >
        <div>
          <Label htmlFor="panel-name">Panel name</Label>
          <Input id="panel-name" required value={panelName} onChange={(e) => setPanelName(e.target.value)} />
        </div>
        {save.error != null && <ErrorNote error={save.error} />}
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * The panel's server-access key for usage metrics (ADR-0012): SSH private
 * key used to reach each server's Sentinel API. Stored encrypted, never
 * re-displayed; the matching public key is installed on the servers.
 */
function MetricsAccessCard() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['metrics', 'status'],
    queryFn: () => api.get<MetricsStatusDto>('/api/metrics/status'),
    retry: false,
  });
  const [sshPrivateKey, setSshPrivateKey] = useState('');

  const save = useMutation({
    mutationFn: () => api.put('/api/metrics/settings', { sshPrivateKey }),
    onSuccess: () => {
      setSshPrivateKey('');
      void queryClient.invalidateQueries({ queryKey: ['metrics'] });
    },
  });

  // Metrics module disabled → the endpoint 404s; hide the card.
  if (status.error) return null;

  return (
    <Card>
      <CardHeader
        title="Usage metrics — server access"
        description="SSH key the collector uses to read Sentinel metrics on each server."
        actions={
          status.data?.sshKeyConfigured ? (
            <Badge tone="green">Key stored</Badge>
          ) : (
            <Badge tone="yellow">No key</Badge>
          )
        }
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-4 px-5 py-4"
      >
        <div>
          <Label htmlFor="metrics-ssh-key">SSH private key</Label>
          <Textarea
            id="metrics-ssh-key"
            rows={4}
            className="font-mono text-xs"
            placeholder={
              status.data?.sshKeyConfigured
                ? '•••••••• (paste a new key to replace the stored one)'
                : '-----BEGIN OPENSSH PRIVATE KEY-----'
            }
            value={sshPrivateKey}
            onChange={(e) => setSshPrivateKey(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Generate a dedicated key (<code>ssh-keygen -t ed25519</code>) and install its public
            half on every server, as the same user Coolify connects with. Stored encrypted
            (AES-256-GCM), never displayed again. Setup guide:{' '}
            <HelpLink topic="metrics/overview">usage metrics &amp; quotas</HelpLink>
            .
          </p>
        </div>
        {save.error != null && <ErrorNote error={save.error} />}
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending} disabled={!sshPrivateKey.trim()}>
            Save key
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function SettingsPage() {
  return (
    <div>
      <PageHeader title="Settings" description="Instance-wide configuration." />
      <div className="grid gap-4 lg:grid-cols-2">
        <CoolifyConnectionCard />
        <PanelSettingsCard />
        <MetricsAccessCard />
        <RecoverySettingsCard />
      </div>
    </div>
  );
}
